import React, { useEffect, useMemo, useState } from 'react';
import { Check, Share2, Plus, X, GripVertical, Trash2, Download, Image as ImageIcon } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { formatMonto, formatFecha, hoyCaracas } from '../utils/format';
import Exportar from '../components/reportes/Exportar';
import BotonAyuda from '../components/BotonAyuda';

const TIPOS = [
  { value: 'materia_prima', label: 'Materia prima' },
  { value: 'intermedio', label: 'Producto intermedio' },
  { value: 'terminado', label: 'Producto terminado' },
  { value: 'servicio', label: 'Servicio' }
];

// Los 6 combos posibles de precio (tipo_precio × tipo_tasa). El orden acá
// manda el orden de columnas en el cuadro final.
const COMBOS = [
  { key: 'detal|USD', tipo_precio: 'detal', tipo_tasa: 'USD', tp: 'Detal', label: 'Detal $' },
  { key: 'detal|BCV', tipo_precio: 'detal', tipo_tasa: 'BCV', tp: 'Detal', label: 'Detal Bs' },
  { key: 'mayor|USD', tipo_precio: 'mayor', tipo_tasa: 'USD', tp: 'Mayor', label: 'Mayor $' },
  { key: 'mayor|BCV', tipo_precio: 'mayor', tipo_tasa: 'BCV', tp: 'Mayor', label: 'Mayor Bs' },
  { key: 'fabrica|USD', tipo_precio: 'fabrica', tipo_tasa: 'USD', tp: 'Fábrica', label: 'Fábrica $' },
  { key: 'fabrica|BCV', tipo_precio: 'fabrica', tipo_tasa: 'BCV', tp: 'Fábrica', label: 'Fábrica Bs' }
];

const A_CONSULTAR = 'a consultar';

function precioTexto(valor, tipo_tasa) {
  return tipo_tasa === 'BCV' ? `Bs ${formatMonto(valor)}` : `$${formatMonto(valor)}`;
}

function precioDe(item, combo) {
  const p = (item.precios || []).find(
    (x) => x.tipo_precio === combo.tipo_precio && x.tipo_tasa === combo.tipo_tasa
  );
  return p ? p.precio : null;
}

// ── Fila de "La lista": presentación elegida, arrastrable, con sus precios
// como pastillas que se prenden/apagan. ─────────────────────────────────────
function FilaLista({ item, combosOn, onToggle, onQuitar, arrastrando, dragHandlers }) {
  const [menuAbierto, setMenuAbierto] = useState(false);

  const conPrecio = COMBOS.filter((c) => precioDe(item, c) != null);
  const consultarOn = COMBOS.filter((c) => combosOn.has(c.key) && precioDe(item, c) == null);
  // "A consultar" solo aplica a los combos en USD (no se ofrece en Bs).
  const disponiblesConsultar = COMBOS.filter(
    (c) => c.tipo_tasa === 'USD' && precioDe(item, c) == null && !combosOn.has(c.key)
  );

  const pill = 'text-xs px-2 py-1 rounded-full whitespace-nowrap transition-colors';

  return (
    <li
      {...dragHandlers}
      className={`flex gap-2 p-3 bg-white ${arrastrando ? 'opacity-40' : ''}`}
    >
      <button
        type="button"
        className="mt-0.5 flex-shrink-0 cursor-grab text-slate-300 hover:text-slate-500 active:cursor-grabbing"
        title="Arrastrar para reordenar"
        aria-label="Arrastrar para reordenar"
      >
        <GripVertical size={16} />
      </button>

      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <p className="text-sm text-slate-700">
            {item.nombre}
            {item.grupo_nombre && <span className="text-slate-400"> · {item.grupo_nombre}</span>}
          </p>
          <button
            type="button"
            onClick={onQuitar}
            title="Quitar de la lista"
            className="flex-shrink-0 text-slate-300 hover:text-red-600 transition-colors"
          >
            <X size={15} />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-1.5">
          {conPrecio.length === 0 && consultarOn.length === 0 && (
            <span className="text-xs text-slate-400">Sin precios cargados —</span>
          )}

          {conPrecio.map((c) => {
            const on = combosOn.has(c.key);
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => onToggle(c.key)}
                className={`${pill} ${on ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
              >
                {c.tp} {precioTexto(precioDe(item, c), c.tipo_tasa)}
              </button>
            );
          })}

          {consultarOn.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => onToggle(c.key)}
              className={`${pill} border border-dashed border-amber-300 bg-amber-50 text-amber-700 inline-flex items-center gap-1`}
            >
              {c.label} · {A_CONSULTAR}
              <X size={11} />
            </button>
          ))}

          {disponiblesConsultar.length > 0 && (
            <div className="relative">
              <button
                type="button"
                onClick={() => setMenuAbierto((v) => !v)}
                className={`${pill} border border-dashed border-slate-300 text-slate-400 hover:text-slate-600`}
              >
                + a consultar
              </button>
              {menuAbierto && (
                <div className="absolute z-10 mt-1 w-40 rounded-md border bg-white py-1 shadow-lg">
                  {disponiblesConsultar.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => { onToggle(c.key); setMenuAbierto(false); }}
                      className="block w-full px-3 py-1.5 text-left text-xs text-slate-600 hover:bg-slate-50"
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

export default function ListaPrecios({ embebido = false } = {}) {
  const { empresaActiva, empresas } = useAuth();
  const empresaNombre = empresas.find((e) => String(e.empresa_id) === String(empresaActiva))?.empresa_nombre;

  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const [busqueda, setBusqueda] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [dispFiltro, setDispFiltro] = useState('venta');

  // producto_id -> Set(comboKey) marcados; `orden` = ids en el orden elegido.
  const [seleccion, setSeleccion] = useState({});
  const [orden, setOrden] = useState([]);
  const [dragId, setDragId] = useState(null);

  const [imgCopiada, setImgCopiada] = useState(false);

  useEffect(() => {
    if (!empresaActiva) return;
    setCargando(true);
    setSeleccion({});
    setOrden([]);
    api
      .get('/producto-precio/lista')
      .then(({ data }) => setItems(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar la lista'))
      .finally(() => setCargando(false));
  }, [empresaActiva]);

  const itemsPorId = useMemo(() => {
    const m = new Map();
    for (const it of items) m.set(it.producto_id, it);
    return m;
  }, [items]);

  function agregar(item) {
    setOrden((prev) => (prev.includes(item.producto_id) ? prev : [...prev, item.producto_id]));
    setSeleccion((prev) => {
      if (prev[item.producto_id]) return prev;
      const conPrecio = COMBOS.filter((c) => precioDe(item, c) != null).map((c) => c.key);
      return { ...prev, [item.producto_id]: new Set(conPrecio) };
    });
  }

  function quitar(id) {
    setOrden((prev) => prev.filter((x) => x !== id));
    setSeleccion((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }

  function vaciar() {
    setOrden([]);
    setSeleccion({});
  }

  function toggleCombo(id, key) {
    setSeleccion((prev) => {
      const set = new Set(prev[id] || []);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...prev, [id]: set };
    });
  }

  function soltarEn(targetId) {
    if (dragId == null || dragId === targetId) { setDragId(null); return; }
    setOrden((prev) => {
      const a = [...prev];
      const from = a.indexOf(dragId);
      const to = a.indexOf(targetId);
      if (from < 0 || to < 0) return prev;
      a.splice(from, 1);
      a.splice(to, 0, dragId);
      return a;
    });
    setDragId(null);
  }

  const catalogo = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return items.filter((it) => {
      if (orden.includes(it.producto_id)) return false;
      if (q && !it.nombre.toLowerCase().includes(q)) return false;
      if (tipoFiltro && it.tipo !== tipoFiltro) return false;
      if (dispFiltro === 'venta' && !['venta', 'ambas'].includes(it.disponibilidad)) return false;
      if (dispFiltro === 'compra' && !['compra', 'ambas'].includes(it.disponibilidad)) return false;
      return true;
    });
  }, [items, orden, busqueda, tipoFiltro, dispFiltro]);

  const elegidos = useMemo(
    () => orden.map((id) => itemsPorId.get(id)).filter(Boolean),
    [orden, itemsPorId]
  );

  // Columnas del cuadro: combos con al menos una celda marcada entre los elegidos.
  const columnas = useMemo(
    () => COMBOS.filter((c) => elegidos.some((it) => seleccion[it.producto_id]?.has(c.key))),
    [elegidos, seleccion]
  );

  const filas = useMemo(
    () =>
      elegidos
        .filter((it) => seleccion[it.producto_id]?.size)
        .map((it) => ({
          nombre: it.nombre,
          celdas: columnas.map((c) => {
            if (!seleccion[it.producto_id]?.has(c.key)) return '';
            const v = precioDe(it, c);
            return v != null ? precioTexto(v, c.tipo_tasa) : A_CONSULTAR;
          })
        })),
    [elegidos, seleccion, columnas]
  );

  // Excel del cuadro: mismos productos y columnas, con los precios como número.
  function contenidoExcel() {
    return {
      titulo: 'Lista de precios',
      hojas: [{
        nombre: 'Lista de precios',
        columnas: [{ titulo: 'Producto' }, ...columnas.map((c) => ({ titulo: c.label, tipo: 'monto' }))],
        filas: elegidos
          .filter((it) => seleccion[it.producto_id]?.size)
          .map((it) => [it.nombre, ...columnas.map((c) => {
            if (!seleccion[it.producto_id]?.has(c.key)) return null;
            const v = precioDe(it, c);
            return v != null ? Number(v) : A_CONSULTAR;
          })])
      }]
    };
  }

  // Dibuja el cuadro en un canvas (2×, fondo blanco) para compartirlo como
  // imagen — el equivalente a copiar celdas de Excel y pegarlas como figura.
  function construirCanvas() {
    const FUENTE = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
    const fBase = `14px ${FUENTE}`;
    const fBold = `600 14px ${FUENTE}`;
    const fTitulo = `700 20px ${FUENTE}`;
    const fSub = `13px ${FUENTE}`;
    const fPie = `italic 11px ${FUENTE}`;
    const padX = 32;
    const padY = 26;
    const gap = 26;
    const rowH = 30;
    const headH = 34;

    const medidor = document.createElement('canvas').getContext('2d');
    medidor.font = fBold;
    const anchoNombre = Math.ceil(
      Math.max(medidor.measureText('Producto').width, ...filas.map((f) => {
        medidor.font = fBase;
        const w = medidor.measureText(f.nombre).width;
        medidor.font = fBold;
        return w;
      }))
    ) + 4;
    const anchoCols = columnas.map((c, i) => {
      medidor.font = fBold;
      let w = medidor.measureText(c.label).width;
      medidor.font = fBase;
      for (const f of filas) w = Math.max(w, medidor.measureText(f.celdas[i] || '').width);
      return Math.ceil(w) + 4;
    });

    const contentW = anchoNombre + anchoCols.reduce((a, b) => a + b + gap, 0);
    const width = Math.max(320, padX * 2 + contentW);

    const alto = padY + (26 + 20 + 8) + headH + rowH * filas.length + 1 + (12 + 14) + padY;

    const dpr = 2;
    const canvas = document.createElement('canvas');
    canvas.width = width * dpr;
    canvas.height = alto * dpr;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.textBaseline = 'middle';

    // Fondo + borde redondeado
    const r = 12;
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.arcTo(width, 0, width, alto, r);
    ctx.arcTo(width, alto, 0, alto, r);
    ctx.arcTo(0, alto, 0, 0, r);
    ctx.arcTo(0, 0, width, 0, r);
    ctx.closePath();
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 1;
    ctx.stroke();

    // Bordes derechos de cada columna (para alinear a la derecha)
    const bordes = [];
    let acum = padX + anchoNombre;
    for (let i = 0; i < columnas.length; i++) {
      acum += gap + anchoCols[i];
      bordes.push(acum);
    }

    let y = padY;
    ctx.fillStyle = '#0f172a';
    ctx.font = fTitulo;
    ctx.textAlign = 'left';
    ctx.fillText('Lista de precios', padX, y + 12);
    y += 26;
    ctx.fillStyle = '#94a3b8';
    ctx.font = fSub;
    ctx.fillText([empresaNombre, formatFecha(hoyCaracas())].filter(Boolean).join(' · '), padX, y + 10);
    y += 20 + 8;

    // Encabezado de tabla
    ctx.fillStyle = '#f8fafc';
    ctx.fillRect(padX - 8, y, width - (padX - 8) * 2, headH);
    ctx.fillStyle = '#64748b';
    ctx.font = fBold;
    ctx.textAlign = 'left';
    ctx.fillText('Producto', padX, y + headH / 2 + 1);
    ctx.textAlign = 'right';
    columnas.forEach((c, i) => ctx.fillText(c.label, bordes[i], y + headH / 2 + 1));
    y += headH;

    // Filas
    filas.forEach((f) => {
      ctx.strokeStyle = '#f1f5f9';
      ctx.beginPath();
      ctx.moveTo(padX - 8, y);
      ctx.lineTo(width - (padX - 8), y);
      ctx.stroke();
      ctx.font = fBase;
      ctx.textAlign = 'left';
      ctx.fillStyle = '#334155';
      ctx.fillText(f.nombre, padX, y + rowH / 2 + 1);
      ctx.textAlign = 'right';
      f.celdas.forEach((s, i) => {
        if (!s) { ctx.fillStyle = '#cbd5e1'; ctx.fillText('—', bordes[i], y + rowH / 2 + 1); return; }
        ctx.fillStyle = s === A_CONSULTAR ? '#b45309' : '#334155';
        ctx.fillText(s, bordes[i], y + rowH / 2 + 1);
      });
      y += rowH;
    });

    y += 12;
    ctx.font = fPie;
    ctx.fillStyle = '#94a3b8';
    ctx.textAlign = 'left';
    ctx.fillText('Precios sujetos a cambio sin previo aviso.', padX, y + 6);

    return canvas;
  }

  function canvasABlob(canvas) {
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  }

  async function copiarImagen() {
    try {
      const blob = await canvasABlob(construirCanvas());
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
        setImgCopiada(true);
        setTimeout(() => setImgCopiada(false), 1500);
      } else {
        descargarBlob(blob);
        alert('Tu navegador no permite copiar imágenes; se descargó el archivo.');
      }
    } catch (err) {
      alert('No se pudo generar la imagen');
    }
  }

  function descargarBlob(blob) {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `lista-precios-${hoyCaracas()}.png`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  async function descargarImagen() {
    try {
      descargarBlob(await canvasABlob(construirCanvas()));
    } catch (err) {
      alert('No se pudo generar la imagen');
    }
  }

  async function compartirImagen() {
    try {
      const blob = await canvasABlob(construirCanvas());
      const file = new File([blob], `lista-precios-${hoyCaracas()}.png`, { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file] });
      } else {
        descargarBlob(blob);
      }
    } catch (err) {
      /* cancelado */
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        {!embebido && (
          <h1 className="text-2xl font-bold text-slate-800">
            Lista de precios
            {empresaNombre && <span className="text-slate-400 font-normal"> · {empresaNombre}</span>}
            <BotonAyuda />
          </h1>
        )}
        <p className="text-sm text-slate-500">
          Agregá presentaciones a la lista, arrastrá para ordenarlas y en cada una dejá encendidos
          solo los precios que querés mostrar. El cuadro de la derecha se arma solo. No incluye costo
          ni margen.
        </p>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(340px,440px)] gap-6 items-start">
        {/* Constructor */}
        <div className="space-y-4 min-w-0">
          {/* Catálogo */}
          <div className="border rounded-lg bg-white">
            <div className="border-b px-3 py-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              <input
                placeholder="Buscar presentación..."
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                className="border rounded-md px-3 py-1.5 text-sm w-full sm:max-w-[200px]"
              />
              <select
                value={tipoFiltro}
                onChange={(e) => setTipoFiltro(e.target.value)}
                className="border rounded-md px-2 py-1.5 text-sm"
              >
                <option value="">Todos los tipos</option>
                {TIPOS.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
              <select
                value={dispFiltro}
                onChange={(e) => setDispFiltro(e.target.value)}
                className="border rounded-md px-2 py-1.5 text-sm"
              >
                <option value="venta">Solo venta</option>
                <option value="compra">Solo compra</option>
                <option value="">Todas</option>
              </select>
            </div>
            <ul className="max-h-72 overflow-y-auto divide-y divide-slate-100">
              {cargando && <li className="p-4 text-center text-slate-400 text-sm">Cargando…</li>}
              {!cargando && catalogo.length === 0 && (
                <li className="p-6 text-center text-slate-400 text-sm">
                  {items.length === 0 ? 'No hay presentaciones cargadas.' : 'Nada para ese filtro (o ya están todas en la lista).'}
                </li>
              )}
              {catalogo.map((it) => {
                const nPrecios = COMBOS.filter((c) => precioDe(it, c) != null).length;
                return (
                  <li key={it.producto_id}>
                    <button
                      type="button"
                      onClick={() => agregar(it)}
                      className="group flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-slate-50"
                    >
                      <Plus size={15} className="flex-shrink-0 text-slate-300 group-hover:text-primary-600" />
                      <span className="min-w-0 flex-1 truncate text-slate-700">
                        {it.nombre}
                        {it.grupo_nombre && <span className="text-slate-400"> · {it.grupo_nombre}</span>}
                      </span>
                      <span className="flex-shrink-0 text-xs text-slate-400">
                        {nPrecios ? `${nPrecios} precio${nPrecios > 1 ? 's' : ''}` : 'sin precio'}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* La lista */}
          <div className="border rounded-lg bg-white">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                La lista · {elegidos.length}
              </span>
              {elegidos.length > 0 && (
                <button
                  type="button"
                  onClick={vaciar}
                  title="Vaciar la lista"
                  className="text-slate-300 hover:text-red-600 transition-colors"
                >
                  <Trash2 size={15} />
                </button>
              )}
            </div>
            {elegidos.length === 0 ? (
              <p className="p-6 text-center text-slate-400 text-sm">
                Todavía no agregaste presentaciones. Elegilas del catálogo de arriba.
              </p>
            ) : (
              <ul className="divide-y divide-slate-100">
                {elegidos.map((it) => (
                  <FilaLista
                    key={it.producto_id}
                    item={it}
                    combosOn={seleccion[it.producto_id] || new Set()}
                    onToggle={(key) => toggleCombo(it.producto_id, key)}
                    onQuitar={() => quitar(it.producto_id)}
                    arrastrando={dragId === it.producto_id}
                    dragHandlers={{
                      draggable: true,
                      onDragStart: () => setDragId(it.producto_id),
                      onDragEnter: (e) => e.preventDefault(),
                      onDragOver: (e) => e.preventDefault(),
                      onDrop: () => soltarEn(it.producto_id),
                      onDragEnd: () => setDragId(null)
                    }}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Salida */}
        <div className="space-y-3 lg:sticky lg:top-4">
          {/* Vista previa en tabla */}
          <div className="border rounded-lg bg-white overflow-hidden">
            <div className="flex items-center justify-between border-b px-3 py-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-400">Vista previa</span>
              <div className="flex items-center gap-1">
                <Exportar excel={contenidoExcel} pdf={false} disabled={filas.length === 0} />
                {typeof navigator !== 'undefined' && navigator.canShare && (
                  <button
                    onClick={compartirImagen}
                    disabled={filas.length === 0}
                    title="Compartir como imagen"
                    className="p-2 rounded-md text-slate-400 hover:text-primary-600 disabled:opacity-40"
                  >
                    <Share2 size={16} />
                  </button>
                )}
                <button
                  onClick={descargarImagen}
                  disabled={filas.length === 0}
                  title="Descargar imagen (PNG)"
                  className="p-2 rounded-md text-slate-400 hover:text-primary-600 disabled:opacity-40"
                >
                  <Download size={16} />
                </button>
                <button
                  onClick={copiarImagen}
                  disabled={filas.length === 0}
                  title="Copiar como imagen"
                  className="p-2 rounded-md text-slate-400 hover:text-primary-600 disabled:opacity-40"
                >
                  {imgCopiada ? <Check size={16} className="text-emerald-600" /> : <ImageIcon size={16} />}
                </button>
              </div>
            </div>
            {filas.length === 0 ? (
              <p className="p-4 text-sm text-slate-400">
                Encendé al menos un precio de una presentación para armar el cuadro.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr>
                      <th className="py-1.5 px-3">Producto</th>
                      {columnas.map((c) => (
                        <th key={c.key} className="py-1.5 px-3 text-right whitespace-nowrap">{c.label}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map((f, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="py-1.5 px-3 text-slate-700">{f.nombre}</td>
                        {f.celdas.map((s, j) => (
                          <td
                            key={j}
                            className={`py-1.5 px-3 text-right tabular-nums whitespace-nowrap ${s === A_CONSULTAR ? 'text-amber-600' : 'text-slate-600'}`}
                          >
                            {s || '—'}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
